import React, { useEffect, useState } from 'react';
import styles from './Showcase.module.css';
import {
  BILLS,
  INITIAL_TABLES,
  IVA_RATE,
  KITCHEN_TICKETS,
  TICKET_STATE_LABEL,
  isLate,
  mmss,
} from './showcaseData';
import type { KitchenTicket, TableState } from './showcaseData';

export interface SceneProps {
  /** Sólo la escena visible mueve sus cronómetros y mesas. */
  active: boolean;
}

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------ */
/* Vapor: plato de pozole humeando                                     */
/* ------------------------------------------------------------------ */

const DOTS_TOP = [80, 100, 120, 140, 160, 180, 200, 220, 240, 260, 280, 300, 320];
const DOTS_BOTTOM = [90, 110, 130, 150, 170, 190, 210, 230, 250, 270, 290, 310];

export const VaporScene: React.FC<SceneProps> = () => (
  <div className={cx(styles.scene, styles.vapor)}>
    <div className={styles.bowlWrap}>
      <svg className={styles.bowl} viewBox="0 0 400 370" aria-hidden="true">
        <g className={styles.steam}>
          <path className={styles.s1} pathLength={100} d="M160 178 C140 148 180 128 160 98 C140 68 180 48 160 18" />
          <path className={styles.s2} pathLength={100} d="M200 172 C180 142 220 122 200 92 C180 62 220 42 200 12" />
          <path className={styles.s3} pathLength={100} d="M240 178 C220 148 260 128 240 98 C220 68 260 48 240 18" />
        </g>
        <ellipse cx="200" cy="352" rx="118" ry="9" className={styles.bowlShadow} />
        <rect x="148" y="312" width="104" height="30" rx="9" fill="#e6dccb" />
        <path d="M60 200 H340 A140 125 0 0 1 60 200 Z" fill="#fbf6ec" />
        <path d="M200 200 H340 A140 125 0 0 1 200 325 Z" className={styles.bowlShade} />
        <g fill="#128078">
          {DOTS_TOP.map((x) => <circle key={x} cx={x} cy="246" r="5" />)}
        </g>
        <g fill="#d9a012">
          {DOTS_BOTTOM.map((x) => <circle key={x} cx={x} cy="263" r="2.6" />)}
        </g>
        <ellipse cx="200" cy="200" rx="140" ry="21" fill="#efe6d4" />
        <ellipse cx="200" cy="202" rx="128" ry="15" fill="#b5351f" />
        <g className={styles.garnish}>
          <circle cx="150" cy="203" r="3.4" fill="#f6efdf" />
          <circle cx="176" cy="207" r="3.6" fill="#f6efdf" />
          <circle cx="222" cy="198" r="3.4" fill="#f6efdf" />
          <circle cx="270" cy="206" r="3" fill="#f6efdf" />
          <circle cx="118" cy="201" r="3" fill="#f6efdf" />
          <circle cx="162" cy="198" r="7" fill="#f8dde0" stroke="#cf475a" strokeWidth="2" />
          <circle cx="240" cy="205" r="6" fill="#f8dde0" stroke="#cf475a" strokeWidth="2" />
          <ellipse cx="196" cy="198" rx="6" ry="2.6" fill="#5f9e3a" transform="rotate(-20 196 198)" />
          <ellipse cx="206" cy="205" rx="5" ry="2.4" fill="#4f8f2f" transform="rotate(25 206 205)" />
          <ellipse cx="258" cy="198" rx="5.5" ry="2.4" fill="#5f9e3a" transform="rotate(10 258 198)" />
          <path d="M286 199 l14 -5 a8 8 0 0 1 -4 12 z" fill="#9cc94a" />
        </g>
      </svg>
    </div>
    <div className={styles.caption}>
      <h2>Tu cocina, siempre caliente.</h2>
      <p>Pedidos, mesas, cocina y caja en un solo lugar.</p>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Comanda: rieles de tickets de cocina con cronómetro                 */
/* ------------------------------------------------------------------ */

const STATE_CLASS = { new: styles.stNew, prep: styles.stPrep, ready: styles.stReady };

const Ticket: React.FC<{ ticket: KitchenTicket; elapsed: number }> = ({ ticket, elapsed }) => {
  const seconds = ticket.state === 'ready' ? ticket.seconds : ticket.seconds + elapsed;
  const late = isLate(ticket, seconds);
  return (
    <article className={cx(styles.ticket, STATE_CLASS[ticket.state])}>
      <header>
        <b>{ticket.title}</b>
        <span className={cx(styles.timer, late && styles.late)}>{mmss(seconds)}</span>
      </header>
      <ul>
        {ticket.items.map(([qty, name]) => (
          <li key={name}><i>{qty}</i><span>{name}</span></li>
        ))}
        {ticket.note && <li className={styles.note}>{ticket.note}</li>}
      </ul>
      <footer>{TICKET_STATE_LABEL[ticket.state]}</footer>
    </article>
  );
};

const Rail: React.FC<{ order: number[]; elapsed: number; reverse?: boolean }> = ({ order, elapsed, reverse }) => {
  const set = (copy: number) => (
    <div className={styles.railSet} aria-hidden={copy === 1 || undefined}>
      {order.map((i) => <Ticket key={i} ticket={KITCHEN_TICKETS[i]} elapsed={elapsed} />)}
    </div>
  );
  return (
    <div className={cx(styles.rail, reverse && styles.railReverse)}>
      {/* Dos copias del mismo set: la marquesina se desplaza -50 % y empalma sin salto */}
      <div className={styles.railTrack}>{set(0)}{set(1)}</div>
    </div>
  );
};

const RAIL_A = [0, 1, 2, 3, 4, 5, 6];
const RAIL_B = [4, 6, 2, 0, 5, 1, 3];

export const KdsScene: React.FC<SceneProps> = ({ active }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active || prefersReducedMotion()) return undefined;
    const id = window.setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  return (
    <div className={cx(styles.scene, styles.kds)}>
      <div className={styles.kdsBar}><span>Cocina · Pantalla 1</span><span className={styles.live}>En vivo</span></div>
      <div className={styles.rails}>
        <Rail order={RAIL_A} elapsed={elapsed} />
        <Rail order={RAIL_B} elapsed={elapsed} reverse />
      </div>
      <div className={styles.caption}>
        <h2>La comanda llega a cocina <em>antes de que el mesero regrese.</em></h2>
        <p>Cada ticket con su cronómetro, como en tu pantalla de cocina.</p>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Mesas: plano del salón cambiando de estado                          */
/* ------------------------------------------------------------------ */

const TABLE_CLASS: Record<TableState, string> = {
  libre: '',
  ocupada: styles.ocupada,
  cuenta: styles.cuenta,
  reservada: styles.reservada,
};

const nextTableState = (state: TableState, roll: number): TableState => {
  if (state === 'libre') return roll < 0.25 ? 'reservada' : 'ocupada';
  if (state === 'reservada') return 'ocupada';
  if (state === 'ocupada') return 'cuenta';
  return 'libre';
};

const TABLE_LAYOUT = INITIAL_TABLES.map((_, n) => {
  const r = Math.floor(n / 5);
  const c = n % 5;
  return { n: n + 1, x: 55 + c * 77.5, y: 100 + r * 82, round: (r + c) % 2 === 0 };
});

export const FloorScene: React.FC<SceneProps> = ({ active }) => {
  const [tables, setTables] = useState<TableState[]>(INITIAL_TABLES);

  useEffect(() => {
    if (!active || prefersReducedMotion()) return undefined;
    const id = window.setInterval(() => {
      // El azar se tira fuera del updater: React puede llamarlo dos veces.
      const i = Math.floor(Math.random() * INITIAL_TABLES.length);
      const roll = Math.random();
      setTables((prev) => prev.map((s, j) => (j === i ? nextTableState(s, roll) : s)));
    }, 1300);
    return () => window.clearInterval(id);
  }, [active]);

  const count = (s: TableState) => tables.filter((t) => t === s).length;

  return (
    <div className={cx(styles.scene, styles.floorScene)}>
      <div className={styles.floorHead}><b>Salón principal</b><span className={styles.live}>En vivo</span></div>
      <svg className={styles.floor} viewBox="0 0 420 340" aria-hidden="true">
        <rect className={styles.floorBg} x="2" y="2" width="416" height="336" rx="14" />
        <rect className={styles.bar} x="40" y="22" width="340" height="22" rx="6" />
        <text className={styles.lbl} x="210" y="37" textAnchor="middle">Barra</text>
        <text className={styles.lbl} x="404" y="328" textAnchor="end">Entrada →</text>
        <text className={styles.lbl} x="16" y="328">Terraza ←</text>
        {TABLE_LAYOUT.map((t, i) => (
          <g key={t.n} className={TABLE_CLASS[tables[i]]}>
            <circle className={styles.ring} cx={t.x} cy={t.y} r="26" />
            {[[t.x - 29, t.y], [t.x + 29, t.y], [t.x, t.y - 29], [t.x, t.y + 29]].map(([x, y]) => (
              <circle key={`${x}-${y}`} className={styles.chair} cx={x} cy={y} r="6.5" />
            ))}
            {t.round ? (
              <circle className={styles.top} cx={t.x} cy={t.y} r="19" />
            ) : (
              <rect className={styles.top} x={t.x - 19} y={t.y - 19} width="38" height="38" rx="6" />
            )}
            <text className={styles.num} x={t.x} y={t.y + 4}>{t.n}</text>
          </g>
        ))}
      </svg>
      <div className={styles.legend}>
        <span>Libres <b>{count('libre')}</b></span>
        <span className={styles.lgOcupada}>Ocupadas <b>{count('ocupada')}</b></span>
        <span className={styles.lgCuenta}>Pidiendo cuenta <b>{count('cuenta')}</b></span>
        <span className={styles.lgReservada}>Reservadas <b>{count('reservada')}</b></span>
      </div>
      <div className={styles.caption}>
        <h2>Todo el salón, de un vistazo.</h2>
        <p>Quién está comiendo, quién pidió la cuenta y qué mesa sigue libre.</p>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Ticket: impresora de caja                                           */
/* ------------------------------------------------------------------ */

const money = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

export const ReceiptScene: React.FC<SceneProps & { playKey: number }> = ({ playKey }) => {
  // Una cuenta distinta en cada entrada a la escena; la impresión dura lo mismo
  // que la escena (--sc-dur), así que el ticket se corta justo al cambiar.
  const bill = BILLS[Math.max(0, playKey) % BILLS.length];
  const total = bill.items.reduce((sum, [qty, , price]) => sum + qty * price, 0);
  const subtotal = total / (1 + IVA_RATE);

  return (
    <div className={cx(styles.scene, styles.receiptScene)}>
      <div className={styles.printer}><span>Caja 1</span><span className={styles.led} /></div>
      <div className={styles.paperWindow}>
        {/* key: cada vez que la escena entra, el ticket vuelve a imprimirse desde arriba */}
        <div key={playKey} className={styles.receipt}>
          <div className={cx(styles.c, styles.big)}>MASSRESTAURANT</div>
          <div className={styles.c}>Sucursal Roma Norte</div>
          <hr />
          <div className={styles.row}><span>{bill.table}</span><span>{bill.guests} {bill.guests === 1 ? 'persona' : 'personas'}</span></div>
          <div className={styles.row}><span>Atendió: {bill.waiter}</span><span>{bill.time}</span></div>
          <div className={styles.row}><span>Folio</span><span>{bill.folio}</span></div>
          <hr />
          {bill.items.map(([qty, name, price]) => (
            <div key={name} className={styles.row}><span>{qty} {name}</span><span>{money.format(qty * price)}</span></div>
          ))}
          <hr />
          <div className={styles.row}><span>Subtotal</span><span>{money.format(subtotal)}</span></div>
          <div className={styles.row}><span>IVA 16%</span><span>{money.format(total - subtotal)}</span></div>
          <div className={cx(styles.row, styles.total)}><span>TOTAL</span><span>{money.format(total)}</span></div>
          <div className={styles.row}><span>Propina sugerida 10%</span><span>{money.format(total * 0.1)}</span></div>
          <hr />
          <div className={styles.c}>¡Gracias por su visita!</div>
          <div className={styles.barcode} />
        </div>
      </div>
      <div className={styles.caption}>
        <h2>Cada cuenta cuadra al cierre.</h2>
        <p>Tickets, propinas e IVA calculados solos.</p>
      </div>
    </div>
  );
};
